import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2024 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : manage transcriber account
// =============================================================================
// ---Third-party imports
import React from 'react';
// ---Framework imports
import { get } from 'ldash';
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { Link } from 'uix/components';
import { useApi, useState, useResettableState, useCallApi, useRouter } from 'uix/hooks';
// ---Local imports
import { TranscriptionLayout } from '../components';
import { useApiWithPuid } from '../hooks';
// ---Globals
// ---Exports
const TranscriberAccountState = ({ i18n, transcriber })=>{
    let { go } = useRouter();
    let [selectedOption, setSelectedOption] = useState(null);
    let handleOptionChange = (event)=>{
        setSelectedOption(event.target.value);
    };
    let [changedName, setChangedName] = useResettableState('', [
        selectedOption
    ]);
    let changeName = (value)=>/*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsx("input", {
                type: "text",
                value: value,
                onChange: (e)=>setChangedName(e.target.value)
            })
        });
    let { callApi } = useCallApi('setTranscriberAuthorship');
    let submitChange = ()=>{
        let visible = selectedOption == 'transcriberChangeToName';
        callApi({
            puid: transcriber.puid,
            name: changedName,
            visible
        }, (data)=>{
            if (data?.status == 'ok') {
                go({
                    updateQuery: {
                        sent: ''
                    }
                });
            }
        });
    };
    let choice = (name)=>/*#__PURE__*/ _jsxs("label", {
            children: [
                /*#__PURE__*/ _jsx("input", {
                    type: "radio",
                    value: name,
                    checked: selectedOption === name,
                    onChange: handleOptionChange
                }),
                i18n.span(name)
            ]
        });
    let anonymous = transcriber.visible === false;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            anonymous ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    choice('transcriberChangeToName'),
                    /*#__PURE__*/ _jsx("br", {}),
                    choice('transcriberChangeToAnonymousAnywhere')
                ]
            }) : choice('transcriberNewToAnonymous'),
            /*#__PURE__*/ _jsx("br", {}),
            selectedOption == 'transcriberChangeToName' && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: i18n.span('transcriberExplainClearname')
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        children: i18n.span('transcriberInsertName')
                    }),
                    changeName(changedName || transcriber.fullname)
                ]
            }),
            [
                'transcriberChangeToAnonymousAnywhere',
                'transcriberNewToAnonymous'
            ].includes(selectedOption) && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: i18n.span('transcriberExplainPseudonym')
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            i18n.span('transcriberInsertNick'),
                            changeName(changedName)
                        ]
                    })
                ]
            }),
            selectedOption && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        onClick: submitChange,
                        children: i18n.get('transcriberSubmitChange')
                    }),
                    ' ',
                    /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>{
                            setSelectedOption(null);
                        },
                        children: i18n.get('transcriberBack')
                    })
                ]
            })
        ]
    });
};
export const TranscriptionAccount = ({ i18n, request })=>{
    // pass through explicit PU_ID
    let sent = request.sent !== undefined;
    let { data } = useApiWithPuid('filter/getTranscriberAccount', {}, {
        autoreset: false,
        deps: [
            sent
        ]
    });
    let transcriber = data?.transcriber || {};
    let anonymous = transcriber.visible === false;
    return /*#__PURE__*/ _jsxs(TranscriptionLayout, {
        children: [
            /*#__PURE__*/ _jsx("h1", {
                children: i18n.span(`transcriberAccountSettings`)
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "accountSettings",
                children: [
                    sent && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                class: "subsec",
                                children: "Ihr Status wurde geändert"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: " Und ihre Angaben wurden registriert. "
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("h2", {
                        className: "subsec",
                        children: i18n.span('transcriberAccountStatus')
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "cell key",
                                children: i18n.span('transcriberAccountEmail')
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "cell",
                                children: transcriber.email
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "cell key",
                                children: i18n.span('__transcriberAccountName')
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "cell",
                                children: transcriber.fullname
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "cell key",
                        children: i18n.span('transcriberAccountCredits')
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "cell",
                        children: i18n.span(anonymous ? 'transcriberPerAnonymous' : 'transcriberPerName')
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                className: "subsec",
                children: i18n.span('transcriberChangeState')
            }),
            sent ? /*#__PURE__*/ _jsx(Link, {
                query: {},
                children: i18n.span('transcriberChangeNew')
            }) : /*#__PURE__*/ _jsx(TranscriberAccountState, {
                transcriber: transcriber,
                i18n: i18n
            })
        ]
    });
};
